import React, { useState } from 'react';
import { FaBolt, FaTimes, FaCube, FaFileInvoice, FaCalendarAlt, FaBatteryHalf, FaChartBar, FaMapMarkerAlt, FaHardHat, FaMap, FaPrint, FaDownload, FaCircle, FaThermometerHalf, FaPlug, FaSlidersH, FaTachometerAlt, FaSun, FaWater } from 'react-icons/fa';
import { SMPSDevice, ModalTab } from '../utils/smpsTypes';
import { getBatteryModeLabel, getStatusLabel, getHealthColor } from '../utils/smpsConstants';
import { exportDeviceDetailToPdf } from '../utils/smpsPdfExport';
import { toast } from 'react-hot-toast';

interface SMPSAssetModalProps {
  isOpen: boolean;
  deviceData: SMPSDevice | null;
  onClose: () => void;
}

const SMPSAssetModal: React.FC<SMPSAssetModalProps> = ({ isOpen, deviceData, onClose }) => {
  const [activeTab, setActiveTab] = useState<ModalTab>('system-info');

  if (!deviceData) return null;

  const { assetDetails, battery, acInput, rectifiers, solar, temperature, alarms, batteryBanks, loads, lvd, thresholds, environment, systemInfo, rawResults } = deviceData;

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!deviceData) return;
    try {
      exportDeviceDetailToPdf(deviceData);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    }
  };

  const tabs: { id: ModalTab; label: string }[] = [
    { id: 'system-info', label: 'System Info' },
    { id: 'power-metrics', label: 'Power Metrics' },
    { id: 'battery-info', label: 'Battery Details' },
    { id: 'load-info', label: 'Load & Energy' },
    { id: 'thresholds', label: 'Thresholds' },
    { id: 'location-info', label: 'Location' },
    { id: 'additional-info', label: 'Additional Info' },
  ];

  return (
    <div className={`modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FaBolt />
            <span>{deviceData.name}</span>
            <span
              className="modal-status-badge"
              style={{ backgroundColor: getHealthColor(deviceData.status), marginLeft: '10px' }}
            >
              {getStatusLabel(deviceData.status)}
            </span>
          </div>
          <button className="close-modal" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="asset-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`asset-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* System Info Tab */}
          <div className={`asset-tab-content ${activeTab === 'system-info' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaCube /> Basic Information</h4>
                <div className="info-item">
                  <div className="info-label">Make:</div>
                  <div className="info-value">{assetDetails.make}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Model:</div>
                  <div className="info-value">{assetDetails.model}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Serial Number:</div>
                  <div className="info-value highlight">{assetDetails.serialNumber}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Capacity:</div>
                  <div className="info-value">{assetDetails.capacity}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaFileInvoice /> Purchase & Warranty</h4>
                <div className="info-item">
                  <div className="info-label">Purchase Date:</div>
                  <div className="info-value">{assetDetails.purchaseDate}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Date:</div>
                  <div className="info-value">{assetDetails.installationDate}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Warranty Expiry:</div>
                  <div className="info-value">{assetDetails.warrantyExpiry}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Warranty Status:</div>
                  <div className={`info-value ${assetDetails.warrantyStatus === 'Active' ? 'success' : 'danger'}`}>
                    {assetDetails.warrantyStatus}
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4><FaCalendarAlt /> Lifecycle Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Codal Life:</div>
                  <div className="info-value">{assetDetails.codalLife}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Remaining Life:</div>
                  <div className="info-value success">{assetDetails.remainingLife}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Age:</div>
                  <div className="info-value">{assetDetails.installationAge}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">IP Address:</div>
                  <div className="info-value highlight">{assetDetails.ipAddress}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Power Metrics Tab */}
          <div className={`asset-tab-content ${activeTab === 'power-metrics' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaBolt /> DC Output</h4>
                <div className="info-item">
                  <div className="info-label">DC Voltage:</div>
                  <div className={`info-value ${deviceData.dcVoltage >= 46 && deviceData.dcVoltage <= 52 ? 'success' : 'warning'}`}>
                    {deviceData.dcVoltage > 0 ? `${deviceData.dcVoltage}V` : '-'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Load Current:</div>
                  <div className="info-value">{deviceData.loadCurrent > 0 ? `${deviceData.loadCurrent}A` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Efficiency:</div>
                  <div className="info-value success">{deviceData.efficiency > 0 ? `${deviceData.efficiency}%` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Active Alarms:</div>
                  <div className={`info-value ${alarms.activeCount > 0 ? 'danger' : 'success'}`}>
                    {alarms.activeCount}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaBatteryHalf /> Battery Status</h4>
                <div className="info-item">
                  <div className="info-label">Battery Voltage:</div>
                  <div className="info-value">{battery.voltage > 0 ? `${battery.voltage}V` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Battery Current:</div>
                  <div className="info-value">{battery.current !== 0 ? `${battery.current}A` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Capacity:</div>
                  <div className={`info-value ${battery.capacity > 50 ? 'success' : 'danger'}`}>
                    {battery.capacity > 0 ? `${battery.capacity}%` : '-'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Mode:</div>
                  <div className={`info-value ${battery.mode === 'FLOAT' ? 'success' : 'warning'}`}>
                    {getBatteryModeLabel(battery.mode)}
                  </div>
                </div>
              </div>
            </div>

            <div className="info-grid">
              <div className="info-section">
                <h4><FaBolt /> AC Input</h4>
                <div className="status-indicators">
                  <div className={`status-indicator-item ${acInput.phaseR > 0 ? 'up' : 'down'}`}>
                    <FaCircle />
                    <span>Phase R: {acInput.phaseR > 0 ? `${acInput.phaseR}V` : 'FAIL'}</span>
                  </div>
                  <div className={`status-indicator-item ${acInput.phaseY > 0 ? 'up' : 'down'}`}>
                    <FaCircle />
                    <span>Phase Y: {acInput.phaseY > 0 ? `${acInput.phaseY}V` : 'FAIL'}</span>
                  </div>
                  <div className={`status-indicator-item ${acInput.phaseB > 0 ? 'up' : 'down'}`}>
                    <FaCircle />
                    <span>Phase B: {acInput.phaseB > 0 ? `${acInput.phaseB}V` : 'FAIL'}</span>
                  </div>
                  <div className="status-indicator-item">
                    <span>Frequency: {acInput.frequency > 0 ? `${acInput.frequency}Hz` : '-'}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaChartBar /> Rectifiers & Power</h4>
                <div className="info-item">
                  <div className="info-label">Rectifiers Online:</div>
                  <div className={`info-value ${rectifiers.online === rectifiers.total ? 'success' : 'warning'}`}>
                    {rectifiers.online}/{rectifiers.total}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Rectifier Power:</div>
                  <div className="info-value">{rectifiers.totalPower > 0 ? `${(rectifiers.totalPower / 1000).toFixed(1)} kW` : '-'}</div>
                </div>
                {solar.slots > 0 && (
                  <div className="info-item">
                    <div className="info-label">Solar Power:</div>
                    <div className="info-value success">{solar.totalPower > 0 ? `${(solar.totalPower / 1000).toFixed(1)} kW` : '-'}</div>
                  </div>
                )}
                <div className="info-item">
                  <div className="info-label">Ambient Temp:</div>
                  <div className={`info-value ${temperature.ambient > 40 ? 'warning' : 'success'}`}>
                    {temperature.ambient > 0 ? `${temperature.ambient}°C` : 'N/A'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Battery Temp:</div>
                  <div className={`info-value ${temperature.battery > 35 ? 'warning' : 'success'}`}>
                    {temperature.battery > 0 ? `${temperature.battery}°C` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4><FaThermometerHalf /> Alarm Status</h4>
              <div className="status-indicators">
                <div className={`status-indicator-item ${alarms.lvdStatus ? 'down' : 'up'}`}>
                  <FaCircle />
                  <span>LVD: {alarms.lvdStatus ? 'ACTIVE' : 'OK'}</span>
                </div>
                <div className={`status-indicator-item ${alarms.overVoltage ? 'down' : 'up'}`}>
                  <FaCircle />
                  <span>Over Voltage: {alarms.overVoltage ? 'ALARM' : 'OK'}</span>
                </div>
                <div className={`status-indicator-item ${alarms.underVoltage ? 'down' : 'up'}`}>
                  <FaCircle />
                  <span>Under Voltage: {alarms.underVoltage ? 'ALARM' : 'OK'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Battery Details Tab */}
          <div className={`asset-tab-content ${activeTab === 'battery-info' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaBatteryHalf /> Battery Overview</h4>
                <div className="info-item">
                  <div className="info-label">Battery Banks:</div>
                  <div className="info-value">{battery.count}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">BMS Units:</div>
                  <div className="info-value">{battery.bmsCount}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Total Capacity:</div>
                  <div className="info-value">{battery.capacityAh > 0 ? `${battery.capacityAh} Ah` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Current Mode:</div>
                  <div className={`info-value ${battery.mode === 'FLOAT' ? 'success' : 'warning'}`}>
                    {getBatteryModeLabel(battery.mode)}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaThermometerHalf /> Battery Temperatures</h4>
                <div className="info-item">
                  <div className="info-label">Sensor 1:</div>
                  <div className={`info-value ${temperature.battery > 35 ? 'warning' : 'success'}`}>
                    {temperature.battery > 0 ? `${temperature.battery}°C` : 'N/A'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Sensor 2:</div>
                  <div className={`info-value ${temperature.battery2 > 35 ? 'warning' : 'success'}`}>
                    {temperature.battery2 > 0 ? `${temperature.battery2}°C` : 'N/A'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Sensor 3:</div>
                  <div className={`info-value ${temperature.battery3 > 35 ? 'warning' : 'success'}`}>
                    {temperature.battery3 > 0 ? `${temperature.battery3}°C` : 'N/A'}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Sensor 4:</div>
                  <div className={`info-value ${temperature.battery4 > 35 ? 'warning' : 'success'}`}>
                    {temperature.battery4 > 0 ? `${temperature.battery4}°C` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {batteryBanks.length > 0 && (
              <div className="info-section">
                <h4><FaBatteryHalf /> Individual Battery Banks</h4>
                <div className="info-grid">
                  {batteryBanks.map((bank, index) => (
                    <div key={index} className="info-section">
                      <h5>Bank {index + 1}</h5>
                      <div className="info-item">
                        <div className="info-label">Voltage:</div>
                        <div className="info-value">{bank.voltage > 0 ? `${bank.voltage}V` : 'N/A'}</div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">Current:</div>
                        <div className="info-value">{bank.current !== 0 ? `${bank.current}A` : 'N/A'}</div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">SOC:</div>
                        <div className={`info-value ${bank.soc > 50 ? 'success' : 'danger'}`}>
                          {bank.soc > 0 ? `${bank.soc}%` : 'N/A'}
                        </div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">Temperature:</div>
                        <div className="info-value">{bank.temperature > 0 ? `${bank.temperature}°C` : 'N/A'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="info-section">
              <h4><FaWater /> Environment</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Ext. Temp 1:</div>
                  <div className="info-value">{environment.temperature1 > 0 ? `${environment.temperature1.toFixed(1)}°C` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Ext. Temp 2:</div>
                  <div className="info-value">{environment.temperature2 > 0 ? `${environment.temperature2.toFixed(1)}°C` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Humidity:</div>
                  <div className="info-value">{environment.humidity > 0 ? `${environment.humidity.toFixed(1)}%` : 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Load & Energy Tab */}
          <div className={`asset-tab-content ${activeTab === 'load-info' ? 'active' : ''}`}>
            <div className="info-section">
              <h4><FaPlug /> Load Outputs</h4>
              <div className="info-grid">
                {loads.map((load, index) => (
                  <div key={index} className="info-section">
                    <h5>Load {index + 1}</h5>
                    <div className="info-item">
                      <div className="info-label">Current:</div>
                      <div className="info-value">{load.current > 0 ? `${load.current.toFixed(2)}A` : '0A'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Today Energy:</div>
                      <div className="info-value">{load.todayEnergy > 0 ? `${(load.todayEnergy / 1000).toFixed(2)} kWh` : '0 kWh'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Total Energy:</div>
                      <div className="info-value">{load.totalEnergy > 0 ? `${(load.totalEnergy / 1000).toFixed(2)} kWh` : '0 kWh'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {solar.slots > 0 && (
              <div className="info-section">
                <h4><FaSun /> Solar Input</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-label">Solar Slots:</div>
                    <div className="info-value">{solar.slots}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Total Power:</div>
                    <div className="info-value success">{solar.totalPower > 0 ? `${(solar.totalPower / 1000).toFixed(2)} kW` : '0 kW'}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="info-section">
              <h4><FaTachometerAlt /> System Info</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Site Name:</div>
                  <div className="info-value">{systemInfo.siteName || 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">System Time:</div>
                  <div className="info-value">{systemInfo.systemTime || 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Power Mode:</div>
                  <div className="info-value">{systemInfo.powerMode || 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Buzzer:</div>
                  <div className={`info-value ${alarms.buzzerEnabled ? 'warning' : 'success'}`}>
                    {alarms.buzzerEnabled ? 'ON' : 'OFF'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Thresholds Tab */}
          <div className={`asset-tab-content ${activeTab === 'thresholds' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaBolt /> DC Voltage Thresholds</h4>
                <div className="info-item">
                  <div className="info-label">High Voltage:</div>
                  <div className="info-value">{thresholds.dcVoltageHigh > 0 ? `${thresholds.dcVoltageHigh}V` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Low Voltage:</div>
                  <div className="info-value">{thresholds.dcVoltageLow > 0 ? `${thresholds.dcVoltageLow}V` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Float Voltage:</div>
                  <div className="info-value">{deviceData.floatVoltage > 0 ? `${deviceData.floatVoltage}V` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Boost Voltage:</div>
                  <div className="info-value">{deviceData.boostVoltage > 0 ? `${deviceData.boostVoltage}V` : 'N/A'}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaBolt /> AC Voltage Thresholds</h4>
                <div className="info-item">
                  <div className="info-label">Over Voltage:</div>
                  <div className="info-value">{thresholds.acOverVoltage > 0 ? `${thresholds.acOverVoltage}V` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Under Voltage:</div>
                  <div className="info-value">{thresholds.acUnderVoltage > 0 ? `${thresholds.acUnderVoltage}V` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Input Type:</div>
                  <div className="info-value">{acInput.inputType || '3-Phase'}</div>
                </div>
              </div>
            </div>

            <div className="info-grid">
              <div className="info-section">
                <h4><FaThermometerHalf /> Temperature Thresholds</h4>
                <div className="info-item">
                  <div className="info-label">Battery Over Temp:</div>
                  <div className="info-value">{thresholds.battOverTemp > 0 ? `${thresholds.battOverTemp}°C` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Battery High Temp:</div>
                  <div className="info-value">{thresholds.battHighTemp > 0 ? `${thresholds.battHighTemp}°C` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Battery Low Temp:</div>
                  <div className="info-value">{thresholds.battLowTemp > 0 ? `${thresholds.battLowTemp}°C` : 'N/A'}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaSlidersH /> LVD Configuration</h4>
                <div className="info-item">
                  <div className="info-label">Battery LVD:</div>
                  <div className={`info-value ${lvd.batteryLvd.enabled ? 'success' : ''}`}>
                    {lvd.batteryLvd.enabled ? 'Enabled' : 'Disabled'} {lvd.batteryLvd.voltage > 0 ? `@ ${lvd.batteryLvd.voltage}V` : ''}
                  </div>
                </div>
                {lvd.loadLvd.map((loadLvdItem, index) => (
                  <div key={index} className="info-item">
                    <div className="info-label">Load LVD {index + 1}:</div>
                    <div className={`info-value ${loadLvdItem.enabled ? 'success' : ''}`}>
                      {loadLvdItem.enabled ? 'Enabled' : 'Disabled'} {loadLvdItem.voltage > 0 ? `@ ${loadLvdItem.voltage}V` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="info-section">
              <h4><FaChartBar /> Rectifier Configuration</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Total Slots:</div>
                  <div className="info-value">{rectifiers.total}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Online:</div>
                  <div className={`info-value ${rectifiers.online === rectifiers.total ? 'success' : 'warning'}`}>
                    {rectifiers.online}/{rectifiers.total}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Current Limit:</div>
                  <div className="info-value">{rectifiers.currentLimit > 0 ? `${rectifiers.currentLimit}A` : 'N/A'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Total Power:</div>
                  <div className="info-value">{rectifiers.totalPower > 0 ? `${(rectifiers.totalPower / 1000).toFixed(2)} kW` : 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Location & Installation Tab */}
          <div className={`asset-tab-content ${activeTab === 'location-info' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaMapMarkerAlt /> Location Details</h4>
                <div className="info-item">
                  <div className="info-label">Station:</div>
                  <div className="info-value highlight">{deviceData.station}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Latitude:</div>
                  <div className="info-value">{assetDetails.latitude}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Longitude:</div>
                  <div className="info-value">{assetDetails.longitude}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Asset Position:</div>
                  <div className="info-value">{assetDetails.assetPosition}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaHardHat /> Installation Details</h4>
                <div className="info-item">
                  <div className="info-label">Installation Team:</div>
                  <div className="info-value">{assetDetails.installationTeam}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Supervisor:</div>
                  <div className="info-value">{assetDetails.supervisor}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Last Maintenance:</div>
                  <div className="info-value">{assetDetails.lastMaintenance}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Next Maintenance Due:</div>
                  <div className="info-value warning">{assetDetails.nextMaintenanceDue}</div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4><FaMap /> Location Map</h4>
              <div className="map-container">
                <div className="map-placeholder">
                  <FaMapMarkerAlt className="map-icon" />
                  <p>Location: {deviceData.station} Station</p>
                  <p>Coordinates: {assetDetails.latitude}, {assetDetails.longitude}</p>
                  <p className="small">Map integration available with GPS coordinates</p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info Tab */}
          <div className={`asset-tab-content ${activeTab === 'additional-info' ? 'active' : ''}`}>
            <div className="info-section">
              <h4><FaCube /> Additional Information</h4>
              <div className="info-grid">
                {
                  Object.keys(rawResults)?.map((item: any, index: number) => (
                    <div className="info-item" key={index}>
                      <div className="info-label">{item}:</div>
                      <div className="info-value">{rawResults[item]?.value}</div>
                    </div>
                  ))
                }
                {/* <div className="info-item">
                  <div className="info-label">Make:</div>
                  <div className="info-value">{assetDetails.make}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Model:</div>
                  <div className="info-value">{assetDetails.model}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Serial Number:</div>
                  <div className="info-value highlight">{assetDetails.serialNumber}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Capacity:</div>
                  <div className="info-value">{assetDetails.capacity}</div>
                </div> */}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SMPSAssetModal;
